import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './user-chip.module.scss';
import { useUser } from '@/hooks/use-user/use-user';
import { PROFILE_SECTION, canAccess } from '@/config/navigation';

interface UserChipProps {
  collapsed?: boolean;
}

export function UserChip({ collapsed = false }: UserChipProps) {
  const { t } = useTranslation(['common', 'layout']);
  const { userData } = useUser();

  if (!userData) return null;

  const fullName = [userData.first_name, userData.last_name].filter(Boolean).join(' ');
  const initials =
    [userData.first_name, userData.last_name]
      .filter(Boolean)
      .map((part) => part[0])
      .join('')
      .toUpperCase() || (userData.email?.[0] ?? '').toUpperCase();
  const roleName = userData.roles?.[0]?.name;
  // The role is an API enum, so its label comes from the catalogue. A role the catalogue does
  // not know yet still reads as words rather than as a raw key path.
  const role = roleName
    ? t(`common:roles.${roleName}`, { defaultValue: roleName.replace(/_/g, ' ') })
    : undefined;

  const chip = (
    <>
      <div className={styles.avatar}>{initials}</div>
      {!collapsed && (
        <div className={styles.identity}>
          <b className={styles.name}>{fullName}</b>
          {role && <span className={styles.role}>{role}</span>}
        </div>
      )}
    </>
  );

  // The chip is the only route into the profile screen, so it goes inert for exactly the
  // roles the route bounces rather than sending them to a redirect.
  if (!canAccess(PROFILE_SECTION, userData)) {
    return <div className={styles.chip}>{chip}</div>;
  }

  return (
    <Link
      to={PROFILE_SECTION.path}
      className={`${styles.chip} ${styles.chipLink}`}
      aria-label={t('layout:userChip.profileLink')}
      title={collapsed ? fullName : undefined}
    >
      {chip}
    </Link>
  );
}
